import {
  acceptsTransportPin,
  recordingPk,
  type RecordingKey,
  type RecordingRow,
  type RecordingUploadTransport,
} from './types';

/** Everything a single writer can land on its own; a create or heal writes all of it. */
export const MIRRORED_FIELDS = ['uploadTransport', 'endDate', 'lastChunkIndex'] as const;

export type MirroredField = (typeof MIRRORED_FIELDS)[number];

/**
 * What the database has not taken: named fields, or 'row' when it holds no row
 * at all. Field writers arm and clear their own part only, so one field's
 * success can never discharge another's — nor the missing row itself.
 */
export type UnpersistedPart = 'row' | MirroredField;

/**
 * Rows this page created, so a live recording's upload path never reads
 * IndexedDB. Earlier pages' rows upload through recovery instead. An empty
 * `unpersisted` means the database has taken this row's state.
 */
interface MirroredRow {
  row: RecordingRow;
  unpersisted: Set<UnpersistedPart>;
}

const rows = new Map<string, MirroredRow>();

/** Separator prevents key collisions ('a'+'bc' vs 'ab'+'c'); ids are numeric/UUID so ':' is safe. */
export const mirrorKey = (key: RecordingKey): string => recordingPk(key).join(':');

export const rememberRecordingRow = (
  row: RecordingRow,
  options?: { persisted?: boolean }
): void => {
  const unpersisted = new Set<UnpersistedPart>();
  if (options?.persisted === false) unpersisted.add('row');
  rows.set(mirrorKey(row), { row, unpersisted });
};

/** No-op for an unknown row — a miss must fall through to the database. */
export const patchRecordingRow = (
  key: RecordingKey,
  patch: Partial<RecordingRow>
): void => {
  const existing = rows.get(mirrorKey(key));
  if (existing) {
    rows.set(mirrorKey(key), { ...existing, row: { ...existing.row, ...patch } });
  }
};

const editParts = (
  key: RecordingKey,
  edit: (parts: Set<UnpersistedPart>) => void
): void => {
  const existing = rows.get(mirrorKey(key));
  if (!existing) return;
  const unpersisted = new Set(existing.unpersisted);
  edit(unpersisted);
  rows.set(mirrorKey(key), { ...existing, unpersisted });
};

/**
 * The heal flushed exactly these parts, carrying `written`'s values. A field the
 * mirror re-armed while the flush awaited holds a newer value than the one that
 * landed, so it stays owed. Returns what the debt actually lost.
 */
export const markHealedPartsPersisted = (
  written: RecordingRow,
  parts: readonly UnpersistedPart[]
): UnpersistedPart[] => {
  const existing = rows.get(mirrorKey(written));
  if (!existing) return [];
  const unpersisted = new Set(existing.unpersisted);
  const settled = parts.filter(
    (part) =>
      unpersisted.has(part) &&
      (part === 'row' || Object.is(existing.row[part], written[part]))
  );
  settled.forEach((part) => unpersisted.delete(part));
  rows.set(mirrorKey(written), { ...existing, unpersisted });
  return settled;
};

/** The database holds no row at all — only a whole-row write may clear this. */
export const markRecordingRowUnpersisted = (key: RecordingKey): void =>
  editParts(key, (parts) => parts.add('row'));

/** This field landed; every other part the mirror still owes stays owed. */
export const markRecordingFieldPersisted = (
  key: RecordingKey,
  field: MirroredField
): void => editParts(key, (parts) => parts.delete(field));

/**
 * The database refused this field. A later write of the SAME field clears it;
 * the heal is what closes the gap when the field stays refused.
 */
export const markRecordingFieldUnpersisted = (
  key: RecordingKey,
  field: MirroredField
): void => editParts(key, (parts) => parts.add(field));

export const peekRecordingRow = (key: RecordingKey): RecordingRow | undefined =>
  rows.get(mirrorKey(key))?.row;

/**
 * The row only while the store still owes it. A deliberate delete leaves the
 * mirror cached until the post-commit forget runs, so a row the store once took
 * is no evidence it holds one now — writing that back would resurrect it.
 */
export const peekUnpersistedRecordingRow = (
  key: RecordingKey
): RecordingRow | undefined => {
  const entry = rows.get(mirrorKey(key));
  return entry?.unpersisted.has('row') ? entry.row : undefined;
};

/**
 * The database holds no row, so every write that needs one misses by
 * construction. Ask this per recording, not the tab-wide memory-only flag, which
 * would silence a genuine orphan on a durable recording.
 */
export const isMemoryBackedRecording = (key: RecordingKey): boolean =>
  rows.get(mirrorKey(key))?.unpersisted.has('row') ?? false;

/** Every row this page mirrors, persisted or not. */
export const listRecordingRows = (): RecordingRow[] =>
  Array.from(rows.values(), (entry) => entry.row);

/** Heal-flush iterator: rows the database has not accepted in full. */
export const listUnpersistedRecordingRows = (): RecordingRow[] =>
  Array.from(rows.values())
    .filter((entry) => entry.unpersisted.size > 0)
    .map((entry) => entry.row);

export const forgetRecordingRow = (key: RecordingKey): void => {
  rows.delete(mirrorKey(key));
};

/** Cleared with the write fence: a closed fence must leave nothing uploadable. */
export const resetRecordingRowCache = (): void => {
  rows.clear();
};

/**
 * Pin arbitration for when the database cannot. A recording the store never took
 * has no cross-page recovery, so session-local first-write-wins is enough, and
 * synchronous, so no concurrent pin can slip between check and write.
 */
export const pinTransportInMirror = (
  key: RecordingKey,
  uploadTransport: RecordingUploadTransport
): 'pinned' | 'missing' | 'rejected' => {
  const existing = rows.get(mirrorKey(key));
  if (!existing) return 'missing';
  if (!acceptsTransportPin(existing.row.uploadTransport, uploadTransport)) {
    return 'rejected';
  }
  rows.set(mirrorKey(key), { ...existing, row: { ...existing.row, uploadTransport } });
  return 'pinned';
};
